import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../../../api/axiosInstance';

export interface VersionChange {
  field: string;
  field_key: string;
  old_value: string | null;
  new_value: string | null;
  type: 'field' | 'column_value' | 'list' | 'checklist';
  column_type?: string;
  added?: string[];
  removed?: string[];
  action?: string;
}

export interface Version {
  id: string;
  tenant_id: string;
  object_type: string;
  object_id: string;
  version_number: number;
  schema_version: number;
  snapshot: Record<string, unknown>;
  changed_fields: string[] | null;
  change_type: 'created' | 'updated' | 'deleted';
  changed_by: string | null;
  changed_by_name: string | null;
  changed_at: string;
  change_reason: string | null;
  /**
   * Business-facing revision ("Revision 2") — set only on versions that ARE a
   * deliberate amendment of a document type that uses revision numbering. Null on
   * ordinary edits, which is most versions.
   */
  revision_number: number | null;
  changes?: VersionChange[];
}

export interface VersionListResponse {
  data: Version[];
  total: number;
  has_more: boolean;
  /**
   * The object's current revision — the number quoted on the document and
   * stamped on its PDF. Served by the API rather than derived from `data`
   * because the list is paginated: with enough ordinary edits since the last
   * amendment, no returned page carries it.
   */
  current_revision: number | null;
}

export interface VersionCompareResponse {
  from: Version;
  to: Version;
  differences: Record<string, { old: unknown; new: unknown }>;
}

async function fetchVersionHistory(
  objectType: string,
  objectId: string,
  limit: number,
  offset: number
): Promise<VersionListResponse> {
  const response = await axiosInstance.get<VersionListResponse>(
    `/api/v1/versions/${objectType}/${objectId}`,
    { params: { limit, offset } }
  );
  return response.data;
}

async function fetchVersionAtTime(
  objectType: string,
  objectId: string,
  timestamp: string
): Promise<{ data: { snapshot: Record<string, unknown>; as_of: string } }> {
  const response = await axiosInstance.get(
    `/api/v1/versions/${objectType}/${objectId}/at`,
    { params: { timestamp } }
  );
  return response.data;
}

async function compareVersions(
  objectType: string,
  objectId: string,
  fromVersion: number,
  toVersion: number
): Promise<VersionCompareResponse> {
  const response = await axiosInstance.get<VersionCompareResponse>(
    `/api/v1/versions/${objectType}/${objectId}/diff`,
    { params: { from: fromVersion, to: toVersion } }
  );
  return response.data;
}

export const versionKeys = {
  all: ['versions'] as const,
  history: (objectType: string, objectId: string) =>
    [...versionKeys.all, 'history', objectType, objectId] as const,
  historyPaged: (objectType: string, objectId: string, limit: number, offset: number) =>
    [...versionKeys.history(objectType, objectId), { limit, offset }] as const,
  compare: (objectType: string, objectId: string, from: number, to: number) =>
    [...versionKeys.all, 'compare', objectType, objectId, from, to] as const,
  atTime: (objectType: string, objectId: string, timestamp: string) =>
    [...versionKeys.all, 'at', objectType, objectId, timestamp] as const,
};

export function useVersionHistory(
  objectType: string,
  objectId: string,
  options?: { limit?: number; offset?: number; enabled?: boolean }
) {
  const { limit = 20, offset = 0, enabled = true } = options ?? {};

  return useQuery({
    queryKey: versionKeys.historyPaged(objectType, objectId, limit, offset),
    queryFn: () => fetchVersionHistory(objectType, objectId, limit, offset),
    enabled: enabled && !!objectType && !!objectId,
  });
}

export function useVersionCompare(
  objectType: string,
  objectId: string,
  fromVersion: number,
  toVersion: number,
  options?: { enabled?: boolean }
) {
  const { enabled = true } = options ?? {};

  return useQuery({
    queryKey: versionKeys.compare(objectType, objectId, fromVersion, toVersion),
    queryFn: () => compareVersions(objectType, objectId, fromVersion, toVersion),
    enabled: enabled && !!objectType && !!objectId && fromVersion > 0 && toVersion > 0,
  });
}

export function useVersionAtTime(
  objectType: string,
  objectId: string,
  timestamp: string,
  options?: { enabled?: boolean }
) {
  const { enabled = true } = options ?? {};

  return useQuery({
    queryKey: versionKeys.atTime(objectType, objectId, timestamp),
    queryFn: () => fetchVersionAtTime(objectType, objectId, timestamp),
    enabled: enabled && !!objectType && !!objectId && !!timestamp,
  });
}
